import React, { FocusEventHandler } from 'react';
import { TSystemActin } from './store';
import { TCommonData, TCommonWidgetValue, TInputData, TItem } from './widget/data';
import { ActionActionTypeEnum } from './widget/enums';
import { TCommonInputOption, TWidgetCommonOption } from './widget/options';
import { TWAction, TWidget, TWidgetSystemMetadata } from './widget/widget';

/**
 * Type representing the possible data types for a player action.
 * It can be a string, number, boolean, common data object, undefined, or null.
 */
export type TPlayerActionData = unknown;

/**
 * Type representing a handler function for a click event.
 *
 * @param {string} uid - The unique ID associated with the clicked item.
 * @param {TItem} value - The item that was clicked.
 */
export type THandleClick = (uid: string, value: TItem) => void;

export type TCheckBoxConfig = {
    skipValidation?: boolean;
};

/**
 * Type representing the state and handler functions for a checkbox component.
 *
 * @property {(e?: React.ChangeEvent<HTMLInputElement>, value?: boolean) => void} handleChange - Handler function for when the checkbox value changes.
 * @property {boolean} isChecked - Indicates whether the checkbox is checked or not.
 */
export type TUseCheckBoxHook = {
    handleChange: (e?: React.ChangeEvent<HTMLInputElement>, value?: boolean) => void;
    isChecked: boolean;
    errorMessage?: TUseInput['errorMessage'];
    isError?: TUseInput['isError'];
    label?: TUseInput['label'];
};

export type TButtonMethodsRef<TD extends TCommonData> = {
    isDisabled: TUseBehavior['isDisabled'];
    triggerActionsByType: TUseTriggerAction<TD>['triggerActionsByType'];
    triggerActionsByNameAndType: TUseTriggerAction<TD>['triggerActionsByNameAndType'];
};

export type TSubmitByEnterMethodsRef = {
    triggerActionsByType: TUseTriggerAction<TCommonData>['triggerActionsByType'];
};

export type TNavigationBlockerViaHistoryMethodsRef = {
    triggerAction: TUseTriggerAction<TCommonData>['triggerAction'];
    systemActionNavigateBack: TSystemActin | undefined;
};

export type TInputMethods = {
    isHidden: TUseBehavior['isHidden'];
    isDisabled: TUseBehavior['isDisabled'];
    triggerActionsByType: TUseTriggerAction<TCommonData>['triggerActionsByType'];
};

export type TListMethodsRef = {
    triggerActionsByType: TUseTriggerAction<TCommonData>['triggerActionsByType'];
    setModal?: TUseAppHook['setModal'];
    setPopup?: TUseAppHook['setPopup'];
    setBottomSheet?: TUseAppHook['setBottomSheet'];
};

export type TMainButtonMethodsRef = {
    triggerAction: TUseTriggerAction<TCommonData>['triggerAction'];
};

/**
 * This hook represents the visual state of the widget.
 *
 * @property {boolean} isDisabled - Return a result og function is enabled/disabled button.
 * @property {boolean} isHidden - Return a result og function is hide or show button.
 */
export type TUseBehavior = {
    isDisabled: boolean;
    isHidden: boolean;
};

/**
 * Type representing the state and handler functions for a list component.
 *
 * @property {THandleClick} handleClick - Handler function for when an item in the list is clicked.
 * @property {boolean} hasReachedEnd - Latch flipped to true when the trigger element
 *   intersects the viewport. Stays true once set.
 */
export type TUseListHook = {
    handleClick: THandleClick;
    hasReachedEnd: boolean;
};
/**
 * Type representing the functions to manage caching behavior for widget data.
 *
 * @template TD - Type of the data to be cached.
 *
 * @property {(data: TD) => Promise<void>} putCache - Function to store data in the cache.
 * @property {() => Promise<TD | null>} getCache - Function to retrieve data from the cache.
 * @property {() => Promise<void>} clearCache - Function to clear the cache.
 */
export type TUseCache<TD> = {
    putCache: (data: TD) => Promise<void>;
    getCache: () => Promise<TD | null>;
    clearCache: () => Promise<void>;
};
/**
 * Type representing the functions to update the value of a widget.
 *
 * @property {(value: TCommonWidgetValue) => void} updateWidgetValue - Function to update the widget value.
 * @property {(value: string, key: string) => void} updateWidgetValueByKey - Function to update the widget value by a specific key.
 */
export type TUseUpdateWidgetValue = {
    updateWidgetValue: (value: TCommonWidgetValue) => void;
    updateWidgetValueByKey: (value: TCommonWidgetValue, key: string) => void;
};

/**
 * Type representing the function to trigger a named action that updates a widget's value.
 *
 * @property {(actionName: string, actionType: ActionActionTypeEnum, value: TCommonWidgetValue) => void} updateWidgetValueViaAction - Function trigger action by name to update any widget via action.
 */
export type TUseUpdateWidgetValueViaAction = {
    updateWidgetValueViaAction: (actionName: string, actionType: ActionActionTypeEnum, value: TCommonWidgetValue) => void;
};

export type TUpdateWidgetValueViaActionMethodsRef = {
    triggerActionsByNameAndType: TUseTriggerAction<TCommonData>['triggerActionsByNameAndType'];
};

/**
 * Type representing the functions to update a mutable widget.
 *
 * @template TD - Type of the data used by the widget.
 * @template TO - Type of the options used by the widget.
 *
 * @property {TWidget<TD, TO>} mutableWidget - The mutable widget instance.
 * @property {(widget: TWidget<TD, TO>) => void} updateWidget - Function to update the widget data and options.
 * @property {(widget: Array<TWidget<TD>>) => void} addMutableWidgets - Function to add multiple mutable widgets.
 */
export type TUseUpdateWidget<TD, TO> = {
    mutableWidget: TWidget<TD, TO>;
    updateWidget: (widget: TWidget<TD, TO>) => void;
    addMutableWidgets: (widget: Array<TWidget<TD>>) => void;
    updateWidgetDataByKey: (key: string, value: unknown) => void;
};

/**
 * Type representing the state of the document scroll detection.
 *
 * @property {boolean} documentScrolled - Indicates whether the document has been scrolled.
 */
export type TUseDetectDocumentScroll = {
    documentScrolled: boolean;
};

/**
 * Type representing the state and functions to manage app-level state and operations.
 *
 * @property {TSetFunc} setModal - Function to open a modal.
 * @property {TSetFunc} setPopup - Function to open a popup.
 * @property {React.Dispatch<React.SetStateAction<string | undefined>>} setBottomSheet - Function to open a bottom sheet.
 * @property {React.Dispatch<React.SetStateAction<string | undefined>>} setModalWindow - Function to open a modal window.
 * @property {string} language - current user language in locale format ru-RU....
 */
export type TUseAppHook = {
    setModal: TSetFunc;
    setPopup: TSetFunc;
    setBottomSheet: React.Dispatch<React.SetStateAction<string | undefined>>;
    setModalWindow: React.Dispatch<React.SetStateAction<string | undefined>>;
    language: string;
};

/**
 * Type representing a function that sets a React state to a specific value.
 */
export type TSetFunc = React.Dispatch<React.SetStateAction<React.ReactNode | null>>;

/**
 * Type representing the function to handle changes in a phone input component.
 *
 * @param {string} value - The new value of the input.
 * @param {Record<string, string>} data - Additional data related to the input.
 * @param {React.ChangeEvent<HTMLInputElement>} [event] - The change event.
 * @param {string} [formattedValue] - The formatted version of the input value.
 */
export type THandleChangeInputPhone = (
    value: string,
    data: Record<string, string | boolean | undefined>,
    event?: React.ChangeEvent<HTMLInputElement>,
    formattedValue?: string,
) => void;

/**
 * Type representing the return value of a mutable widget.
 *
 * @template TD - Type of the data used by the widget.
 * @template TO - Type of the options used by the widget. Defaults to `TCommonOption`.
 *
 * @property {TWidget<TD, TO>} widget - The widget instance that uses the provided data and options.
 */
export type TUseGetWidget<TD, TO = TWidgetCommonOption> = {
    widget?: TWidget<TD, TO>;
};

/**
 * Type representing the state and handlers for an input component.
 *
 * @property {boolean} [isFocused] - Indicates if the input is currently focused.
 * @property {boolean} [isFilled] - Indicates if the input has been filled with a value.
 * @property {boolean} [isError] - Indicates if there is an error state for the input.
 * @property {string} [errorMessage] - The error message to display when there is an error.
 * @property {string} [label] - The label message to display label for input.
 * @property {string} [helperText] - The helperText message to display label for input.
 * @property {string} value - The current value of the input.
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} [handleChange] - Handler function for when the input value changes. Receives the change event and an optional function as arguments.
 * @property {FocusEventHandler} [handleBlur] - Handler function for when the input loses focus.
 * @property {FocusEventHandler} [handleFocus] - Handler function for when the input gains focus.
 * @property {(item: TItem) => void} handleClickDropdown - Handler function for when an item in the dropdown is clicked. Receives the clicked item as an argument.
 */
export type TUseInput = {
    isFocused?: boolean;
    isFilled?: boolean;
    isError?: boolean;
    errorMessage?: string;
    label?: string;
    helperText?: string;
    value: string;
    ref?: React.RefObject<HTMLInputElement | null>;
    failedValidationInternalId?: string | null;
    handleChange?: (e?: React.ChangeEvent<HTMLInputElement>, value?: string) => void;
    handleBlur?: FocusEventHandler;
    handleFocus?: FocusEventHandler;
    handleClickDropdown: (item: TItem, options?: { doNotValidate?: boolean }) => void;
    defaultErrorMessage?: string;
    systemMetadata: TWidgetSystemMetadata;
    setError: (value: boolean) => void;
    setErrorMessage: (value?: string) => void;
    setFailedValidationInternalId?: (value: string | null) => void;
    updateInputValue: (value: unknown, uid?: string) => void;
} & TUseBehavior;

export type TInputConfig = {
    parseValue?: (value: string, widget: TWidget<TInputData, TCommonInputOption>) => string;
    onChange?: (value: string, event?: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event?: React.FocusEvent<Element>) => void;
    onFocus?: (event?: React.FocusEvent<Element>) => void;
    onBeforeSelect?: () => void;
    onSelect?: (item: TItem, value: string) => void;
    getValidationValue?: (value: unknown) => string;
    skipValidation?: boolean;
};

/**
 * Type representing the state and handlers for a phone input component.
 *
 * @property {string} iso2 - The ISO 3166-1 alpha-2 country code.
 * @property {THandleChangeInputPhone} handleChangeInputPhone - Handler function for changes in the phone input.
 */
export type TUseInputPhone = {
    iso2: string;
    handleChangeInputPhone: THandleChangeInputPhone;
};

export type TPlayerActionOption = {
    toastMessage?: string;
    textForCopyToClipboard?: string;
};
export type TPlayerActionHandlers = Partial<
    Record<string, (action: TWAction, options?: TPlayerActionOption, value?: TPlayerActionData, finalValue?: TPlayerActionData) => void | Promise<void>>
>;
export type TUseTriggerActionOptions = {
    isShowLoader?: boolean;
    isUpdateWidgetByUid?: boolean;
    playerActionOptions?: TPlayerActionOption;
    value?: TPlayerActionData;
    widgetAction?: TWidget<TCommonData>;
    keepalive?: boolean;
    forceScenario?: boolean;
    skipDelay?: boolean;
};

export type TUseTriggerAction<TD extends TCommonData> = {
    triggerAction: (action: TWAction, options?: TUseTriggerActionOptions) => Promise<TWidget<TD> | undefined>;
    triggerActionsByType: (type: ActionActionTypeEnum | string, options?: TUseTriggerActionOptions) => Promise<TWidget<TD> | undefined>;
    triggerActionsByNameAndType: (type: ActionActionTypeEnum | string, name: string, options?: TUseTriggerActionOptions) => Promise<TWidget<TD> | undefined>;
};

/**
 * Type representing the return value of the useGlobalVariables hook.
 * It's a record of variable names to their string values.
 */
export type TUseGlobalVariables = Readonly<Record<string, string>>;
